/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import moment from "moment";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";

export function getClientColor(clientId) {
  if (!clientId) return "#535353";

  let hash = 0;

  for (let i = 0; i < clientId.length; i += 1) {
    hash = (hash * 31 + clientId.charCodeAt(i)) % 2147483647;
  }
  const hue = Math.abs(hash) % 360;

  return `hsl(${hue}, 60%, 35%)`;
}

export function startOfDayInTZMs(dateInput, timeZone) {
  const dayStr = moment(dateInput).format("YYYY-MM-DD");

  if (moment.tz) return moment.tz(`${dayStr} 00:00`, "YYYY-MM-DD HH:mm", timeZone).valueOf();

  return moment(`${dayStr} 00:00`, "YYYY-MM-DD HH:mm").valueOf();
}

export function addHoursInTZMs(dayStartMsUTC, hours, timeZone) {
  if (moment.tz) return moment.tz(dayStartMsUTC, timeZone).add(hours, "hours").valueOf();
  return moment(dayStartMsUTC).add(hours, "hours").valueOf();
}

export function formatDateInTZ(date, timeZone, formatStr = "HH:mm DD/MM/YYYY") {
  if (!date) return "";
  if (moment.tz) return moment.tz(date, timeZone).format(formatStr);
  return moment(date).format(formatStr);
}

export function buildTimelineEventContent({
  title,
  isActive,
  start,
  end,
  timeZone,
  labelStart = "Inicio",
  labelEnd = "Final",
}) {
  const startText = formatDateInTZ(start, timeZone);
  const endText = formatDateInTZ(end, timeZone);

  return {
    domNodes: [
      Object.assign(document.createElement("div"), {
        style: "width: 100%; height: 100%; display: flex; align-items: center; padding-left: 6px;",
        innerHTML: `
            <div style="display: flex; flex-direction: column; gap: 2px;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="width: 15px; height: 15px; border-radius: 50%; background: ${
                  isActive ? "#2ecc71" : "#7f8c8d"
                };"></span>
                <span style="font-size: 13px; font-weight: 600;">${title}</span>
              </div>
              ${
                startText
                  ? `<div style="font-size: 12px; opacity: 0.9;">${labelStart}: ${startText}</div>`
                  : ""
              }
              ${
                endText
                  ? `<div style="font-size: 12px; opacity: 0.9;">${labelEnd}: ${endText}</div>`
                  : ""
              }
            </div>
          `,
      }),
    ],
  };
}

export function getReservationsList(reservationsDict) {
  return reservationsDict;

  // return Object.values(reservationsDict || {});
}

export function getClientDisplayName(client) {
  if (!client) return "";
  return `${client.firstName || ""} ${client.lastName || ""}`.trim();
}

function compareText(a, b) {
  return `${a || ""}`.trim().localeCompare(`${b || ""}`.trim(), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function compareClientsByLastNameAndFirstName(clientA, clientB) {
  const byLastName = compareText(clientA?.lastName, clientB?.lastName);
  if (byLastName !== 0) return byLastName;
  return compareText(clientA?.firstName, clientB?.firstName);
}

export function sortReservationsByClientLastNameAndFirstName(reservations, clients) {
  return [...(reservations || [])].sort((reservationA, reservationB) => {
    const clientA = clients?.[reservationA?.clientId] || {};
    const clientB = clients?.[reservationB?.clientId] || {};
    return compareClientsByLastNameAndFirstName(clientA, clientB);
  });
}

export function buildClientReservationSelectorOptions({
  reservations,
  clients,
  apartments,
  formatTimestamp,
  getTimeZone,
}) {
  const sortedReservations = sortReservationsByClientLastNameAndFirstName(reservations, clients);

  return {
    values: sortedReservations.map((reservation) => reservation?.uid),
    names: sortedReservations.map((reservation) => {
      const client = clients?.[reservation?.clientId] || {};
      const apartment = apartments?.[reservation?.apartmentId] || {};
      const timeZone = getTimeZone?.(reservation) || "UTC";

      const fullName = `${client?.lastName || ""}, ${client?.firstName || ""}`;
      const apartmentName = apartment?.name || reservation?.apartmentId || "-";
      const startLabel = formatTimestamp?.(reservation?.startDate, timeZone) || "";
      const endLabel = formatTimestamp?.(reservation?.endDate, timeZone) || "";

      return {
        uid: reservation?.uid,
        name: `${fullName || "-"} - ${apartmentName} (${startLabel} - ${endLabel})`,
      };
    }),
  };
}

export function buildAppliedLabel({
  appliedProjectId,
  appliedApartmentId,
  appliedProject,
  appliedApartment,
}) {
  if (!appliedProjectId || !appliedApartmentId) return "";

  const projectName = appliedProject?.name || appliedProjectId;
  const apartmentName = appliedApartment?.name || appliedApartmentId;

  return `${projectName} — ${apartmentName}`;
}

export function isReservationActive(reservation, clients, now = Date.now()) {
  const { startDate, endDate } = reservation || {};

  if (!Number.isFinite(startDate) || !Number.isFinite(endDate)) return false;

  const isInAcceptedPeriodTime = startDate < now && endDate > now;

  return isInAcceptedPeriodTime;
}

export function buildTimelineEvents({
  reservations,
  appliedProjectId,
  appliedApartmentId,
  clients,
}) {
  if (!Array.isArray(reservations)) return [];

  return reservations
    .filter((clientReservation) => {
      if (!clientReservation) return false;
      if (appliedProjectId && clientReservation?.projectId !== appliedProjectId) return false;
      if (appliedApartmentId && clientReservation?.apartmentId !== appliedApartmentId) return false;
      return true;
    })
    .filter((clientReservation) => clientReservation?.startDate && clientReservation?.endDate)
    .map((clientReservation) => {
      const client = clientReservation?.clientId ? clients?.[clientReservation?.clientId] : null;
      const clientName = getClientDisplayName(client);

      const active = isReservationActive(clientReservation, clients);
      const color = getClientColor(clientReservation?.clientId);

      return {
        id: clientReservation?.uid,
        title: clientName,
        start: new Date(clientReservation?.startDate).toISOString(),
        end: new Date(clientReservation?.endDate).toISOString(),
        display: "block",
        backgroundColor: color,
        borderColor: color,
        textColor: "#fff",
        className: "event-info",
        extendedProps: {
          uid: clientReservation?.uid,
          projectId: clientReservation?.projectId,
          apartmentId: clientReservation?.apartmentId,
          clientId: clientReservation?.clientId,
          isActive: active,
        },
      };
    });
}

export function getApartments(projects, projectId) {
  if (projects && projectId && Object.hasOwnProperty.call(projects, projectId)) {
    if (Object.hasOwnProperty.call(projects[projectId], "apartments")) {
      return Object.keys(projects[projectId].apartments);
    }
  }
  return [];
}

export function getApartmentsNames(projects, projectId, apartments) {
  if (projects && projectId && Object.hasOwnProperty.call(projects, projectId)) {
    if (Object.hasOwnProperty.call(projects[projectId], "apartments")) {
      const apartmentIds = Object.keys(projects[projectId].apartments);

      const getApartmentNumber = (name) => {
        const match = (name || "").toString().match(/\d+/);
        return match ? Number.parseInt(match[0], 10) : null;
      };

      return Object.values(apartments || {})
        .filter(({ apartmentId: newApartmentId }) => apartmentIds.indexOf(newApartmentId) >= 0)
        .map(({ apartmentId: newApartmentId, name }) => ({ uid: newApartmentId, name }))
        .sort((a, b) => {
          const nameA = (a?.name || "").toString();
          const nameB = (b?.name || "").toString();

          const aptNumA = getApartmentNumber(nameA);
          const aptNumB = getApartmentNumber(nameB);

          if (Number.isFinite(aptNumA) && Number.isFinite(aptNumB)) {
            if (aptNumA !== aptNumB) return aptNumA - aptNumB;
            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
          }
          if (Number.isFinite(aptNumA)) return -1;
          if (Number.isFinite(aptNumB)) return 1;

          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });
    }
  }
  return [];
}

const CREATE_CLIENT_VALUE = "__CREATE_CLIENT__";

export function getClients(clients) {
  const clientIds = Object.values(clients || {})
    .sort(compareClientsByLastNameAndFirstName)
    .map((client) => client?.uid)
    .filter(Boolean);

  return [CREATE_CLIENT_VALUE, ...clientIds];
}

export function getClientsNames(clients, t) {
  const createClientOption = {
    uid: CREATE_CLIENT_VALUE,
    name: t("createClient"),
  };

  const clientOptions = Object.values(clients || {})
    .sort(compareClientsByLastNameAndFirstName)
    .map((client) => ({
      uid: client.uid,
      name:
        `${client.firstName || ""} ${client.lastName || ""}`.trim() || client.email || client.uid,
    }));

  return [createClientOption, ...clientOptions];
}

export function CellDate({ value }) {
  return <span>{moment(value).format("DD/MM/YYYY HH:mm")}</span>;
}

export function CellActive({ value }) {
  const isActive = Boolean(value);

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center">
      <MDBox
        component="span"
        title={isActive ? "Active" : "Inactive"}
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          display: "inline-block",
          backgroundColor: isActive ? "#2ecc71" : "#7f8c8d",
        }}
      />
    </MDBox>
  );
}

CellDate.defaultProps = {
  value: 0,
};

CellDate.propTypes = {
  value: PropTypes.any,
};

CellActive.defaultProps = {
  value: false,
};

CellActive.propTypes = {
  value: PropTypes.any,
};

export function buildReservationColumns(t) {
  const columns = [
    { Header: t("active"), accessor: "active", Cell: CellActive, maxWidth: 15 },
    { Header: t("project"), accessor: "projectName", maxWidth: 20 },
    { Header: t("apartment"), accessor: "apartmentName", maxWidth: 20 },
    { Header: t("startDate"), accessor: "startDate", Cell: CellDate, maxWidth: 10 },
    { Header: t("endDate"), accessor: "endDate", Cell: CellDate, maxWidth: 10 },
    { Header: t("First Name"), accessor: "firstName", width: 20 },
    { Header: t("Last Name"), accessor: "lastName", width: 20 },
    { Header: "Email", accessor: "email", maxWidth: 20 },
    { Header: "key Password", accessor: "keyboardPwd", maxWidth: 10 },
    { Header: "keyboard Id", accessor: "keyboardPwdId", maxWidth: 10 },
    { Header: t("createdAt"), accessor: "createdAt", Cell: CellDate, maxWidth: 10 },
  ];

  return columns;
}

export function buildAvailabilityColumns(t) {
  return [
    { Header: t("project"), accessor: "projectName", width: 25 },
    { Header: t("apartment"), accessor: "apartmentName", width: 25 },
    { Header: "Onna ID", accessor: "onnaId", maxWidth: 20 },
    { Header: t("state"), accessor: "state", maxWidth: 15 },
  ];
}

function compareNames(a, b) {
  return `${a || ""}`.localeCompare(`${b || ""}`, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function buildAvailabilityRows({
  projects,
  apartments,
  clientReservations,
  selectedProjectId,
  selectedStartTimestamp,
  selectedEndTimestamp,
  t,
}) {
  if (!Number.isFinite(selectedStartTimestamp) || !Number.isFinite(selectedEndTimestamp)) {
    return [];
  }

  if (selectedEndTimestamp <= selectedStartTimestamp) {
    return [];
  }

  const overlappingReservations = (clientReservations || []).filter((reservation) => {
    const { startDate, endDate } = reservation || {};
    if (!Number.isFinite(startDate) || !Number.isFinite(endDate)) return false;
    if (selectedProjectId && reservation?.projectId !== selectedProjectId) return false;
    return startDate < selectedEndTimestamp && endDate > selectedStartTimestamp;
  });

  const occupiedApartmentIds = new Set(
    overlappingReservations.map((reservation) => reservation?.apartmentId).filter(Boolean)
  );

  const availableRows = [];

  Object.values(projects || {}).forEach((project) => {
    if (!project?.uid) return;
    if (selectedProjectId && project.uid !== selectedProjectId) return;

    const projectApartmentIds = Object.keys(project?.apartments || {});

    projectApartmentIds.forEach((apartmentId) => {
      if (occupiedApartmentIds.has(apartmentId)) return;

      const apartment = apartments?.[apartmentId];
      const apartmentName = apartment?.name || apartmentId;

      availableRows.push({
        uid: apartmentId,
        projectId: project.uid,
        apartmentId,
        projectName: project?.name || project.uid,
        apartmentName,
        onnaId: apartment?.onnaId || "-",
        state: t("available"),
      });
    });
  });

  return availableRows.sort((a, b) => {
    const byProject = compareNames(a?.projectName, b?.projectName);
    if (byProject !== 0) return byProject;
    return compareNames(a?.apartmentName, b?.apartmentName);
  });
}

export function buildClientsColumns(t) {
  return [
    { Header: t("First Name"), accessor: "firstName", width: 20 },
    { Header: t("Last Name"), accessor: "lastName", width: 20 },
    { Header: "Email", accessor: "email", maxWidth: 20 },
    {
      Header: t("createdAt"),
      accessor: "createdAt",
      Cell: CellDate,
      maxWidth: 10,
    },
  ];
}

export function buildReservationRow(
  reservation,
  { clients, projects, apartments, now = Date.now(), clientOverride = null } = {}
) {
  if (!reservation) return null;

  const { uid, projectId, apartmentId, clientId, lockData, startDate, endDate } = reservation;

  const isActive = isReservationActive({ uid, clientId, startDate, endDate }, clients, now);

  const client = clientId && clients ? clients[clientId] : null;

  const firstName = clientOverride?.firstName ?? client?.firstName ?? "";
  const lastName = clientOverride?.lastName ?? client?.lastName ?? "";
  const email = clientOverride?.email ?? client?.email ?? "";

  return {
    ...reservation,
    firstName,
    lastName,
    email,
    apartmentName: apartments?.[apartmentId]?.name || reservation.apartmentName || apartmentId,
    projectName: projects?.[projectId]?.name || reservation.projectName || projectId,
    keyboardPwd: lockData?.keyboardPwd || "NO",
    keyboardPwdId: lockData?.keyboardPwdId || "NO",
    active: isActive,
  };
}

export function buildReservationSubmitData(data, { projects, apartments, projectId, apartmentId }) {
  const { startDate, endDate } = data;

  let lockIdToSend = data?.lockId || "";
  let projectName = data?.projectName;

  let { apartmentName } = data;

  if (Object.hasOwnProperty.call(projects || {}, projectId)) {
    projectName = projects[projectId]?.name || projectName;
  }

  const apt = apartments?.[apartmentId] || {};
  apartmentName = apt?.name || apartmentName;

  if (apt?.cloudApis?.accessControl?.configured) {
    lockIdToSend = apt.cloudApis.accessControl?.lockId || lockIdToSend;
  }

  return {
    ...data,
    projectName,
    lockId: lockIdToSend,
    startDate,
    endDate,
    apartmentName,
  };
}

export function isOmnitecConfigured(apartments, apartmentId) {
  if (!Object.hasOwnProperty.call(apartments || {}, apartmentId)) return false;
  const { cloudApis } = apartments[apartmentId];
  if (!cloudApis?.accessControl?.configured) return false;
  return cloudApis.accessControl?.accessControlSystem === "omnitec";
}

export function buildClientTabsInfo(t) {
  return [
    { label: t("availability"), icon: "event_available" },
    { label: t("clientReservationsActives"), icon: "check_circle" },
    { label: t("clientReservationsAll"), icon: "list" },
    { label: t("clientReservationsTimeline"), icon: "calendar_month" },
    { label: t("clients"), icon: "group" },
  ];
}

export function datesIsCorrect(values) {
  const { startDate, endDate } = values || {};

  if (!startDate || !endDate) {
    return false;
  }

  const startMs = moment(startDate).valueOf();
  const endMs = moment(endDate).valueOf();

  return Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs;
}
